import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Cookies from "js-cookie";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select";
import { fetchAllTimesheetsByMonthForAdminQueryOptions } from "@/config/queries/query-options";
import { ColumnDef } from "@tanstack/react-table";
import { SalaryTimesheet } from "./_components/timesheet-table/schema";
import { getColumns } from "./_components/timesheet-table/columns";
import { DataTable } from "./_components/timesheet-table/data-table";
import { CreateSalaryModal } from "./_components/create-salary-modal";
import { SubmitTimesheetModal } from "./_components/submit-timesheet-modal";
import { EditTimesheetModal } from "./_components/edit-timesheet-modal";
import { SubmitTimesheetWithCsvModal } from "./_components/submit-timesheet-with-csv";

type Month = {
  label: string;
  value: number;
  disabled: boolean;
};

const AllMonths = [
  { label: "January", value: 0, disabled: false },
  { label: "February", value: 1, disabled: false },
  { label: "March", value: 2, disabled: false },
  { label: "April", value: 3, disabled: false },
  { label: "May", value: 4, disabled: false },
  { label: "June", value: 5, disabled: false },
  { label: "July", value: 6, disabled: false },
  { label: "August", value: 7, disabled: false },
  { label: "September", value: 8, disabled: false },
  { label: "October", value: 9, disabled: false },
  { label: "November", value: 10, disabled: false },
  { label: "December", value: 11, disabled: false },
] satisfies Month[];

function updateMonthsAvailability(
  months: Month[],
  selectedYear: number
): Month[] {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  return months.map((month) => {
    if (selectedYear === currentYear) {
      return {
        ...month,
        disabled: month.value >= currentMonth,
      };
    } else if (selectedYear < currentYear) {
      return {
        ...month,
        disabled: false,
      };
    } else {
      return {
        ...month,
        disabled: true,
      };
    }
  });
}

// const getCurrentYear = () => {
//   return new Date().getFullYear();
// };

function generateYears(): number[] {
  const currentYear = new Date().getFullYear();
  const endYear = 2024;
  const years: number[] = [];

  for (let year = currentYear; year >= endYear; year--) {
    years.push(year);
  }

  return years;
}

const SalaryTimesheetsPageAdmin = () => {
  const key = Cookies.get("key") as string;
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth() - 1
  );

  const months = useMemo(() => {
    const result = updateMonthsAvailability(AllMonths, selectedYear);

    return result;
  }, [selectedMonth, selectedYear]);

  const years = useMemo(() => generateYears(), []);

  const { data, isPending, isFetching } = useQuery(
    fetchAllTimesheetsByMonthForAdminQueryOptions(key, {
      year: selectedYear,
      monthName: months?.find((t) => t?.value === selectedMonth)
        ?.label as string,
    })
  );

  const sheets = useMemo(() => {
    if (isPending) {
      return [];
    }

    if (data === undefined || data?.sheets === undefined) {
      return [];
    }

    const result = data?.sheets?.map((sal) => {
      return {
        id: sal?._id,
        month: sal?.month,
        year: sal?.year,
        present: sal?.present,
        absent: sal?.absent,
        createdAt: sal?.createdAt,
        paidLeave: sal?.paidLeave,
        unpaidLeave: sal?.unpaidLeave,
        employeeName: sal?.employee?.name,
        salaryGenerated: sal?.salaryGenerated,
        createdFromCSV: sal?.createdFromCSV,
      };
    });

    return result?.sort(
      (a, b) =>
        new Date(b?.createdAt as string).getTime() -
        new Date(a?.createdAt as string).getTime()
    );
  }, [data, isPending, isFetching]);

  const columns = useMemo<ColumnDef<SalaryTimesheet, unknown>[]>(
    () => getColumns(),
    []
  );

  return (
    <div className="p-5 h-[calc(100%-56px)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-2xl font-medium">Employees' Salary Timesheets</h3>

        <div className="flex items-center gap-4">
          <SubmitTimesheetWithCsvModal />
          <SubmitTimesheetModal />
        </div>
      </div>
      <div className="flex items-center gap-2 justify-end">
        <div>
          <Select
            value={String(selectedMonth)}
            onValueChange={(value) => {
              setSelectedMonth(Number(value));
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a month" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectGroup>
                {months.map((month) => {
                  return (
                    <SelectItem
                      key={month.value}
                      value={String(month.value)}
                      disabled={month.disabled}
                    >
                      {month.label}
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col md:flex-row gap-3 items-center">
          <div className="flex-1">
            <Select
              value={String(selectedYear)}
              onValueChange={(value) => {
                setSelectedYear(Number(value));
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a year" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <SelectGroup>
                  {years.map((year) => {
                    return (
                      <SelectItem key={year} value={String(year)}>
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="mt-5 mb-10 pb-10">
        <DataTable data={sheets} columns={columns} />
      </div>

      <CreateSalaryModal />

      <EditTimesheetModal />
    </div>
  );
};

export default SalaryTimesheetsPageAdmin;
